import { FC, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { GroupBase, OptionsOrGroups } from "react-select";
import { EditListingColumnsButton } from "../../../components/modalEditListingColumns";
import {
  useGetDeviceListingQuery,
  useGetFileRetrievalListingQuery,
  useLazyGetDeviceListingQuery,
} from "../../../api/network/camerasAdminVMApiService";
import {
  DeviceListingResponse,
  DeviceSnapshot,
} from "../../../api/types/Camera";
import svgIcons from "../../../assets/svg/svgIcons";
import {
  AdminFormFieldAsyncLazyDropdown,
  AdminFormFieldDropdown,
  AdminFormFieldSubmit,
  TSelectboxOption,
} from "../../../components/admin/formFields";
import AdminTable from "../../../components/adminTable";
import {
  ModalEditListingColumns,
  TListingColumn,
} from "../../../components/modalEditListingColumns";
import Pagination, {
  TPaginationSelected,
} from "../../../components/pagination";
import { TAdminTableRowData } from "../../../components/types";
import { APP_CONFIG } from "../../../constants/constants";
import { useAccountTimezone } from "../../../hooks/timezone";
import { asyncDebounce } from "../../../utils/common";
import {
  dbTimeToJsTime,
  formattedDateTimeBasedOnTimezone,
  getStartEndOfDayInUTC,
  jsTimeToDBTime,
} from "../../../utils/dateTime";
import {
  localStorageKeys,
  useLocalStorage,
} from "../../../utils/localStorageItems";
import {
  all_columns_file_retrieval,
  FILE_RETRIEVAL_TASK_STATUS_FILTER_OPTIONS,
  FILE_RETRIEVEL_STATE_TASK,
} from "../constants";

import { fromZonedTime } from "date-fns-tz";
import { useDispatch, useSelector } from "react-redux";
import { DateRange } from "rsuite/esm/DateRangePicker";
import { useLazyOrganizationDriversQuery } from "../../../api/network/adminApiServices";
import {
  setListingQueryParams,
  setModalsData,
  TListingQueryParams,
  TModalsState,
} from "../../../api/store/commonSlice";
import { TLegacyDriver } from "../../../api/types/Driver";
import { routeUrls } from "../../../navigation/routeUrls";
import { useLoggedInUserData } from "../../../utils/user";
import FormFieldDateRangePicker from "./dateRangePicker";
import ShowSnapshots from "./modalShowSnapshot";
import { consistentDisplayAssetItem } from "../../../utils/vehicle";

const ScreenVideoHistoryFileRetrieval: FC = () => {
  const { t } = useTranslation("translation", {
    keyPrefix: "camera.dashboard.management.cameras",
  });

  const thisUserOrganizationId = useLoggedInUserData("ownerOrganizationId");
  const thisOrgDriverAllowed = useLoggedInUserData("hasdrivers");
  const showDriver = thisOrgDriverAllowed === "1";
  const { convertToTZForDateFns, tzForDisplay, tzForDateFns } =
    useAccountTimezone();
  const dispatch = useDispatch();
  const modalsState = useSelector(
    (state: { commonReducer: { modals: TModalsState } }) =>
      state.commonReducer.modals
  );
  const [currentRowDataSnapshots, setCurrentRowDataSnapshots] = useState<
    DeviceSnapshot[]
  >([]);

  // columns
  const { getLocalStorageItem } = useLocalStorage();
  const savedColumns = getLocalStorageItem(
    localStorageKeys.columns.FileRetrieval
  );

  // Update columns initialization
  const filteredColumns = useMemo(
    () =>
      all_columns_file_retrieval.filter(
        (col) => showDriver || col !== "Driver"
      ),
    [showDriver]
  );
  const [columns, setColumns] = useState<TListingColumn[]>(
    filteredColumns.map((colItem) => ({
      name: colItem,
      show: !!savedColumns ? savedColumns.includes(colItem) : true,
    }))
  );
  // update columns when showDriver changes
  useEffect(() => {
    setColumns(
      filteredColumns.map((colItem) => ({
        name: colItem,
        show: !!savedColumns ? savedColumns.includes(colItem) : true,
      }))
    );
  }, [filteredColumns]);

  const from = useMemo(() => {
    const refDateStart = new Date();
    refDateStart.setMonth(refDateStart.getMonth() - 1);
    refDateStart.setDate(1);
    const refDateStartStringInTimezone = formattedDateTimeBasedOnTimezone(
      refDateStart.getTime(),
      tzForDateFns,
      "yyyy-MM-dd"
    );
    const { startOfDayInUTC } = getStartEndOfDayInUTC(
      refDateStartStringInTimezone,
      tzForDateFns
    );
    return jsTimeToDBTime(startOfDayInUTC.getTime());
  }, []);
  const to = useMemo(() => {
    const refDateEnd = new Date();
    refDateEnd.setMonth(refDateEnd.getMonth() + 1);
    refDateEnd.setDate(0);
    const refDateEndStringInTimezone = formattedDateTimeBasedOnTimezone(
      refDateEnd.getTime(),
      tzForDateFns,
      "yyyy-MM-dd"
    );
    const { endOfDayInUTC } = getStartEndOfDayInUTC(
      refDateEndStringInTimezone,
      tzForDateFns
    );
    return jsTimeToDBTime(endOfDayInUTC.getTime());
  }, []);
  // const [fileRetrievalQueryParams, setFileRetrievalQueryParams] =
  //   useState<FileRetrievalListingPayload>({
  //     keyword: "",
  //     offset: 0,
  //     limit: APP_CONFIG.LISTINGS.DEFAULT_PAGE_SIZE,
  //     page: 1,
  //     serial_number: "",
  //     since: from,
  //     until: to,
  //     task_state_filter: "",
  //   });

  // const [selectedCameraDeviceOption, setSelectedCameraDeviceOption] =
  //   useState<TSelectboxOption | null>(null);
  const listingQueryParams: TListingQueryParams = useSelector(
    (state: any) => state.commonReducer.listingQueryParams
  );
  const {
    deviceFiles: fileRetrievalQueryParams,
    deviceFileSelectedCameraOption,
    deviceFileSelectedDriverOption,
  } = listingQueryParams;

  useEffect(() => {
    if (
      fileRetrievalQueryParams.since === 0 &&
      fileRetrievalQueryParams.until === 0
    ) {
      dispatch(
        setListingQueryParams({
          ...listingQueryParams,
          deviceFiles: {
            ...fileRetrievalQueryParams,
            // if start_time is not null, we assume list query is initialized
            since: from,
            until: to,
          },
        })
      );
    }
  }, [dispatch, fileRetrievalQueryParams, from, listingQueryParams, to]);

  const [cameraDeviceListForSelectLabel, setCameraDeviceListForSelectLabel] =
    useState<TSelectboxOption[]>([]);

  const commonDeviceListingParams = {
    keyword: "",
    model: "",
    status: "",
    offset: 0,
    limit: APP_CONFIG.LISTINGS.XL_PAGE_SIZE,
    page: 1,
    linked_to_asset: 1,
  };

  // fetching camera device listing
  const {
    data: dataInitialDeviceListing,
    isFetching: isFetchingInitialDeviceListing,
    error,
  } = useGetDeviceListingQuery(commonDeviceListingParams, {
    // refetchOnMountOrArgChange: true, // caching removed to ensure listing always shows correct status
  });

  useEffect(() => {
    if (
      !!dataInitialDeviceListing &&
      !isFetchingInitialDeviceListing &&
      deviceFileSelectedCameraOption === null &&
      deviceFileSelectedDriverOption === null
    ) {
      const initiallySelectedItem = !!dataInitialDeviceListing?.data?.[0]
        ?.serial_number
        ? {
            value: dataInitialDeviceListing?.data?.[0]?.serial_number,
            label: dataInitialDeviceListing?.data?.[0]?.unit_name,
          }
        : null;
      // setSelectedCameraDeviceOption(initiallySelectedItem);
      dispatch(
        setListingQueryParams({
          ...listingQueryParams,
          deviceFileSelectedCameraOption: initiallySelectedItem,
        })
      );
    }
  }, [
    dataInitialDeviceListing,
    isFetchingInitialDeviceListing,
    deviceFileSelectedCameraOption,
  ]);

  const [
    getDeviceListingAPITrigger,
    {
      isLoading: isLoadingLazyDeviceListing,
      isFetching: isFetchingLazyDeviceListing,
    },
  ] = useLazyGetDeviceListingQuery();
  const loadOptionsHandlerDevice = async (
    inputValue: string,
    prevOptions: OptionsOrGroups<unknown, GroupBase<unknown>>,
    additional?: { page: number }
  ) => {
    const { data: deviceListingResponse } = await getDeviceListingAPITrigger({
      ...commonDeviceListingParams,
      keyword: inputValue,
      // page: additional?.page || 1,
      offset:
        (additional?.page ? additional.page - 1 : 0) *
        commonDeviceListingParams.limit,
    });

    if (!deviceListingResponse || !deviceListingResponse?.data) {
      return {
        options: [],
        hasMore: false,
        additional: {
          page: 1,
        },
      };
    }
    const { data: results } = deviceListingResponse as {
      data: DeviceListingResponse[];
    };
    const options = !!results
      ? (results || ([] as DeviceListingResponse[])).map(
          (item: DeviceListingResponse, index: number) =>
            ({
              value: `${item.serial_number}`,
              //label: `${item.unit_name}${item?.ft_device_id ? ` | ${item.ft_device_id}` : ""}${item?.ft_device_description ? ` | ${item.ft_device_description}` : ""}${item?.displayname ? ` | ${item.displayname}` : ""}`,
              label: `${consistentDisplayAssetItem(item.ft_device_id, item.ft_device_description, item.ft_displayname, item.drivername, showDriver)}`,
            }) as TSelectboxOption
        )
      : [];
    setCameraDeviceListForSelectLabel([
      ...cameraDeviceListForSelectLabel,
      // ensure unique values
      ...(options as TSelectboxOption[]).filter(
        (option) =>
          !cameraDeviceListForSelectLabel.some(
            (existingOption) => existingOption.value === option.value
          )
      ),
    ]);
    return {
      options,
      hasMore: false, // TODO: NEED TO CHECK // deviceListingResponse?.next ? true : false,
      additional: {
        page: (additional?.page as number) + 1 || 2,
      },
    };
  };
  const debouncedLoadOptionsHandlerDevice = asyncDebounce(
    loadOptionsHandlerDevice,
    APP_CONFIG.DEBOUNCE.SEARCH
  );

  // fetching file retrieval list
  const {
    data: dataFileRetrievalListing,
    isFetching: isFetchingFileRetrievalListing,
  } = useGetFileRetrievalListingQuery(
    {
      ...fileRetrievalQueryParams,
      keyword: deviceFileSelectedCameraOption?.value || "",
      // either serial_number or driver_id should be available for this API
      ...(!!deviceFileSelectedCameraOption?.value
        ? {
            serial_number: deviceFileSelectedCameraOption?.value,
          }
        : {}),
      ...(!!deviceFileSelectedDriverOption?.value
        ? {
            driver_id: deviceFileSelectedDriverOption?.value,
          }
        : {}),
      ...(deviceFileSelectedCameraOption === null &&
      deviceFileSelectedDriverOption === null
        ? {
            serial_number: "",
          }
        : {}),
      since: fileRetrievalQueryParams?.since
        ? fileRetrievalQueryParams?.since
        : 0, //TODO need to fix date and time
      until: fileRetrievalQueryParams?.until
        ? fileRetrievalQueryParams?.until
        : 0, //TODO need to fix date and time
      time_lapse: false,
      offset:
        (fileRetrievalQueryParams.page - 1) * fileRetrievalQueryParams.limit,
    },
    {
      skip:
        (!deviceFileSelectedCameraOption && !deviceFileSelectedDriverOption) ||
        !fileRetrievalQueryParams?.since ||
        !fileRetrievalQueryParams?.until,
      refetchOnMountOrArgChange: true,
    }
  );

  const { count, data: results } = dataFileRetrievalListing || {};

  const onParkingPhotoClick = (images: DeviceSnapshot[]) => {};

  //table data
  const tableData: TAdminTableRowData[] = !!results
    ? (results || []).map((item, index) => ({
        navLink: item.ticket
          ? `${routeUrls.dashboardChildren.cameraChildren.recordings}/${item.ticket}`
          : "",
        stateData: item,
        cellData: [
          <img
            src={svgIcons.ShowImageIcon}
            alt="show-image"
            className={`p-2 bg-gray-100 cursor-pointer rounded-md hover:bg-accent-blue-pale`}
            onClick={(e) => {
              e.stopPropagation();
              setCurrentRowDataSnapshots(item.images || []);
              dispatch(
                setModalsData({ ...modalsState, showSnapShotsView: true })
              );
            }}
          />, // Snapshots
          item?.cameraid ?? "-", // Camera ID
          item?.ft_device_id ?? "-", // Device ID
          item?.ft_device_description ?? "-", // Asset Description
          item?.ft_displayname ?? "-", // Display Name
          ...(showDriver ? [item?.drivername || item?.driverid || "-"] : []), // Driver
          formattedDateTimeBasedOnTimezone?.(
            dbTimeToJsTime(item?.retrieve_time),
            tzForDateFns,
            APP_CONFIG.DATETIME_FORMATS.DISPLAY.DATE_TIME
          ) ?? "-", // Retrieval Date and Time
          <span className={FILE_RETRIEVEL_STATE_TASK[item?.task_state]?.color}>
            {FILE_RETRIEVEL_STATE_TASK[item?.task_state]?.label ?? "-"}
          </span>, // Status
          item?.recording_start_time
            ? formattedDateTimeBasedOnTimezone?.(
                dbTimeToJsTime(item?.recording_start_time ?? 0),
                tzForDateFns,
                APP_CONFIG.DATETIME_FORMATS.DISPLAY.DATE_TIME
              )
            : "-", // Recording Start Time
        ].filter((_, index) => columns[index].show),
      }))
    : [];

  const handleRangeChange = (value: [any, any] | null) => {
    if (value) {
      const [start, end] = value;
      if (!start || !end || isNaN(start.getTime()) || isNaN(end.getTime())) {
        // Invalid date detected, abort further logic
        return;
      }
      //   const sinceTimestamp = jsTimeToDBTime(start); //TODO not correct
      //   const untilTimestamp = jsTimeToDBTime(end); //TODO not

      const utcSinceDate = fromZonedTime(
        start,
        convertToTZForDateFns(tzForDateFns)
      );
      const jsTimeSince = new Date(utcSinceDate).getTime();
      const utcUntilDate = fromZonedTime(
        end,
        convertToTZForDateFns(tzForDateFns)
      );
      const jsTimeUntil = new Date(utcUntilDate).getTime();
      // setFileRetrievalQueryParams((prev) => ({
      //   ...prev,
      //   since: jsTimeToDBTime(jsTimeSince),
      //   until: jsTimeToDBTime(jsTimeUntil),
      //   page: 1,
      // }));
      dispatch(
        setListingQueryParams({
          ...listingQueryParams,
          deviceFiles: {
            ...fileRetrievalQueryParams,
            // if start_time is not null, we assume list query is initialized
            since: jsTimeToDBTime(jsTimeSince),
            until: jsTimeToDBTime(jsTimeUntil),
          },
        })
      );
    }
  };

  // INTENTIONALLY using vehicle API to get drivers as here we will need to show the drivers
  // from those vehicles that have a camera associated with them
  // const {
  //   data: dataInitialCameraVehicleDriversList,
  //   isFetching: isFetchingInitialCameraVehicleDriversList,
  // } = useOrganizationVehiclesQuery({
  //   accountid: `${thisUserOrganizationId}`,
  //   page: 1,
  //   page_size: APP_CONFIG.LISTINGS.DEFAULT_PAGE_SIZE,
  //   search: "",
  //   linked_to_camera: true,
  //   linked_to_driver: true,
  // });
  // const [orgVehiclesQuery, { isLoading: isLazyLoadingVehicles }] =
  //   useLazyOrganizationVehiclesQuery();
  // Update 9/10/2025 - reverting from vehicles API back to drivers API for file retrieval page
  const [orgDriversQuery, { isLoading: isLazyLoadingDrivers }] =
    useLazyOrganizationDriversQuery();
  const loadOptionsHandlerDriver = async (
    inputValue: string,
    prevOptions: OptionsOrGroups<unknown, GroupBase<unknown>>,
    additional?: { page: number }
  ) => {
    // console.log("CALL prevOptions", prevOptions);
    const { data: cameraVehicleDriversQueryResponse } = await orgDriversQuery({
      accountid: `${thisUserOrganizationId}`,
      page: additional?.page ?? 1,
      page_size: APP_CONFIG.LISTINGS.DROPDOWN_SEARCH_PAGE_SIZE,
      search: inputValue,
      // search_fields: APP_CONFIG.ENTITY_SEARCH_FIELDS.DRIVER_DROPDOWN.join(","), // search by driverid only
      // disabled the above `search_fields` param, as we are intentionally calling vehicle API to get drivers, special set of `search_fields` will be used here:
      // search_fields:
      //   APP_CONFIG.ENTITY_SEARCH_FIELDS.DEVICE_DRIVER_SEARCH.join(","),
      search_fields: APP_CONFIG.ENTITY_SEARCH_FIELDS.DRIVER_DROPDOWN.join(","), // search by driverid only
      isactive: 1,
      // linked_to_camera: true,
      // linked_to_driver: true,
    });

    if (
      !cameraVehicleDriversQueryResponse ||
      !cameraVehicleDriversQueryResponse?.results
    ) {
      return {
        options: [],
        hasMore: false,
        additional: {
          page: additional?.page || 1,
        },
      };
    }
    const { results } = cameraVehicleDriversQueryResponse as {
      results: TLegacyDriver[];
    };
    return {
      options: !!results
        ? (results || ([] as TLegacyDriver[])).map(
            (item: TLegacyDriver, index: number) =>
              ({
                value: `${item.driverid}`,
                label: `${item.description || item.driverid}`, // driverDisplayName(item), //  || item.displayname
              }) as TSelectboxOption
          )
        : [],
      hasMore: cameraVehicleDriversQueryResponse?.next ? true : false,
      additional: {
        page: additional?.page ? (additional?.page as number) + 1 : 2,
      },
    };
  };
  const debouncedLoadOptionsHandlerDriver = asyncDebounce(
    loadOptionsHandlerDriver,
    APP_CONFIG.DEBOUNCE.SEARCH
  );

  const isDisabled = !fileRetrievalQueryParams?.task_state_filter;
  // && !deviceFileSelectedDriverOption;
  return (
    <div className="px-5 pt-4 bg-gray-100">
      <div className="w-full Xgrid Xgrid-cols-12 flex gap-4">
        {/* Filter by Camera/Device */}
        <AdminFormFieldAsyncLazyDropdown
          key={`selected_${JSON.stringify(deviceFileSelectedCameraOption)}`}
          loadingData={
            isFetchingInitialDeviceListing ||
            isLoadingLazyDeviceListing ||
            isFetchingLazyDeviceListing
          }
          label={false}
          id="camera_device_id"
          name="camera_device_id"
          placeholder={t("search_placeholder_camera")}
          // TODO: NEEDS FIXING - enabling this is causing page 1 to show 2 times
          // defaultOptions={
          //   dataInitialDeviceListing?.data?.map((cameraDevice) => ({
          //     value: cameraDevice?.serial_number,
          //     label: cameraDevice?.unit_name,
          //   })) || [] // Ensuring a fallback to an empty array if results are undefined
          // }
          additional={{ page: 1 }}
          defaultOption={
            !!deviceFileSelectedCameraOption
              ? deviceFileSelectedCameraOption.value === "-"
                ? undefined
                : deviceFileSelectedCameraOption
              : undefined
          }
          loadOptions={debouncedLoadOptionsHandlerDevice}
          isClearable={
            !!deviceFileSelectedCameraOption && !!deviceFileSelectedDriverOption
          }
          onChange={(e: any) => {
            dispatch(
              setListingQueryParams({
                ...listingQueryParams,
                deviceFileSelectedCameraOption: e,
                deviceFileSelectedDriverOption: null,
              })
            );
          }}
          // disabled={
          //   deviceFileSelectedCameraOption === null &&
          //   isFetchingInitialDeviceListing
          // }
          // detailsFormField={true}
          customWrapperClass="flex-grow min-w-36 mt-1"
        />

        {/* Filter by Driver */}
        {showDriver && (
          <AdminFormFieldAsyncLazyDropdown
            key={`driver_id_filter_${JSON.stringify(deviceFileSelectedDriverOption)}`}
            customWrapperClass="flex-grow min-w-36 mt-1"
            loadingData={isLazyLoadingDrivers}
            label={false}
            id="driver_id"
            name="driver_id"
            value={
              !!deviceFileSelectedDriverOption
                ? deviceFileSelectedDriverOption.value
                : undefined
            } // passed undefined to let placeholder display if no value is selected
            placeholder={t("driver_id_placeholder")}
            // defaultOptions={
            //   dataInitialCameraVehicleDriversList?.results?.map(
            //     (item: TLegacyVehicle) => ({
            //       value: `${item.driverid}`,
            //       label: `${item.driverid}${item.drivername}`, // driverDisplayName(item),
            //     })
            //   ) || undefined
            // }
            defaultOption={
              !!deviceFileSelectedDriverOption
                ? {
                    value: deviceFileSelectedDriverOption.value,
                    label: deviceFileSelectedDriverOption.label, // values.driver_name ?? values.driver_id,
                  }
                : undefined
            } // passed undefined to let placeholder display if no value is selected
            additional={{ page: 1 }}
            isClearable={
              !!deviceFileSelectedCameraOption &&
              !!deviceFileSelectedDriverOption
            }
            HideDropdownIndicator={false}
            loadOptions={debouncedLoadOptionsHandlerDriver}
            noOptionsMessage={() => t("driver_id_no_item")}
            onChange={(e) => {
              if (APP_CONFIG.DEBUG.ALL) console.log("driver_id", e);
              dispatch(
                setListingQueryParams({
                  ...listingQueryParams,
                  deviceFileSelectedDriverOption: e,
                  deviceFileSelectedCameraOption: null,
                })
              );
            }}
          />
        )}
        {APP_CONFIG.DEBUG.ALL && (
          <>
            <pre className="text-xs">
              {JSON.stringify(fileRetrievalQueryParams, null, 2)}
            </pre>
            <span className="w-48 text-xs">
              from:{from}
              <br />
              {!!from
                ? formattedDateTimeBasedOnTimezone(
                    dbTimeToJsTime(parseInt(`${from}`)),
                    tzForDateFns,
                    "MM/dd/yyyy (hh:mmaa)"
                  )
                : ""}
              <br />
              <br />
              to:{to}
              <br />
              {!!to
                ? formattedDateTimeBasedOnTimezone(
                    dbTimeToJsTime(parseInt(`${to}`)),
                    tzForDateFns,
                    "MM/dd/yyyy (hh:mmaa)"
                  )
                : ""}
              <br />
              <br />
              <span
                className={
                  fileRetrievalQueryParams?.since !== from ? "text-red-700" : ""
                }
              >
                Start:{fileRetrievalQueryParams?.since}
                <br />
                {!!fileRetrievalQueryParams?.since
                  ? formattedDateTimeBasedOnTimezone(
                      dbTimeToJsTime(
                        parseInt(`${fileRetrievalQueryParams?.since}`)
                      ),
                      tzForDateFns,
                      "MM/dd/yyyy (hh:mmaa)"
                    )
                  : ""}
              </span>
              <br />
              <br />
              <span
                className={
                  fileRetrievalQueryParams?.until !== to ? "text-red-700" : ""
                }
              >
                End:{fileRetrievalQueryParams?.until}
                <br />
                {!!fileRetrievalQueryParams?.until
                  ? formattedDateTimeBasedOnTimezone(
                      dbTimeToJsTime(
                        parseInt(`${fileRetrievalQueryParams?.until}`)
                      ),
                      tzForDateFns,
                      "MM/dd/yyyy (hh:mmaa)"
                    )
                  : ""}
              </span>
            </span>
          </>
        )}
        <FormFieldDateRangePicker
          value={
            fileRetrievalQueryParams?.since && fileRetrievalQueryParams?.until
              ? ([
                  //TODO: need fixing
                  new Date(
                    formattedDateTimeBasedOnTimezone?.(
                      dbTimeToJsTime(fileRetrievalQueryParams?.since),
                      tzForDateFns,
                      APP_CONFIG.DATETIME_FORMATS.DISPLAY.DATE_TIME
                    )
                  ),
                  new Date(
                    formattedDateTimeBasedOnTimezone?.(
                      dbTimeToJsTime(fileRetrievalQueryParams?.until),
                      tzForDateFns,
                      APP_CONFIG.DATETIME_FORMATS.DISPLAY.DATE_TIME
                    )
                  ),
                  // new Date(dbTimeToJsTime(fileRetrievalQueryParams?.since)),
                  // new Date(dbTimeToJsTime(fileRetrievalQueryParams?.until)),
                ] as DateRange)
              : null
          }
          onChange={handleRangeChange}
        />
        <AdminFormFieldDropdown
          id="status-select"
          label=""
          name={t("select_status_name")}
          placeholder={t("select_status_placeholder")}
          value={fileRetrievalQueryParams?.task_state_filter}
          onChange={(option) => {
            if (option) {
              // setFileRetrievalQueryParams({
              //   ...fileRetrievalQueryParams,
              //   task_state_filter: option?.value,
              //   page: 1,
              // });
              dispatch(
                setListingQueryParams({
                  ...listingQueryParams,
                  deviceFiles: {
                    ...fileRetrievalQueryParams,
                    task_state_filter: option?.value,
                    page: 1,
                  },
                })
              );
            }
          }}
          customWrapperClass="flex-grow"
          dimensionClass="w-full h-11"
          options={FILE_RETRIEVAL_TASK_STATUS_FILTER_OPTIONS}
          key={`status-select-${fileRetrievalQueryParams?.task_state_filter}`}
        />
        <AdminFormFieldSubmit
          buttonDimensionClass={"h-11 w-full px-2 group"}
          customWrapperClass="w-28 mt-1"
          type="button"
          variant="primary"
          label={
            <span className="flex justify-center whitespace-nowrap">
              {t("clear")}
              {
                <img
                  className={`ml-2 ${!isDisabled ? "transition-all group-focus:grayscale-0 group-focus:brightness-0 group-focus:invert group-hover:grayscale-0 group-hover:brightness-0 group-hover:invert" : ""} h-5 w-5`}
                  src={isDisabled ? svgIcons.CloseIconGray : svgIcons.CloseIcon}
                  alt={"clear"}
                />
              }
            </span>
          }
          onClick={() => {
            // setFileRetrievalQueryParams({
            //   ...fileRetrievalQueryParams,
            //   task_state_filter: "",
            //   page: 1,
            // });
            dispatch(
              setListingQueryParams({
                ...listingQueryParams,
                deviceFiles: {
                  ...fileRetrievalQueryParams,
                  task_state_filter: "",
                  page: 1,
                },
                // deviceFileSelectedDriverOption: null,
              })
            );
          }}
          disabled={isDisabled}
        />
      </div>
      <ModalEditListingColumns
        columns={columns}
        setColumns={setColumns}
        lsKey={localStorageKeys.columns.FileRetrieval}
      />
      <div className="py-4 relative">
        <EditListingColumnsButton />
        <AdminTable
          viewportHeightOffset={"400"}
          columns={columns
            .filter((item) => item?.show)
            .map((item) => item.name)}
          data={tableData}
          isLoading={isFetchingFileRetrievalListing}
        />
        {!isFetchingFileRetrievalListing && (
          <Pagination
            pageSize={fileRetrievalQueryParams.limit}
            handlePageSizeChange={(e: TSelectboxOption | null) => {
              // setFileRetrievalQueryParams({
              //   ...fileRetrievalQueryParams,
              //   page: 1,
              //   limit: parseInt(`${e?.value}`),
              // });
              dispatch(
                setListingQueryParams({
                  ...listingQueryParams,
                  deviceFiles: {
                    ...fileRetrievalQueryParams,
                    page: 1,
                    limit: parseInt(`${e?.value}`),
                  },
                })
              );
            }}
            totalPages={
              count ? Math.ceil(count / fileRetrievalQueryParams.limit) : 1
            }
            forcePage={fileRetrievalQueryParams.page - 1}
            handlePageClick={(data: TPaginationSelected) => {
              // setFileRetrievalQueryParams({
              //   ...fileRetrievalQueryParams,
              //   page: data?.selected + 1,
              // });
              dispatch(
                setListingQueryParams({
                  ...listingQueryParams,
                  deviceFiles: {
                    ...fileRetrievalQueryParams,
                    page: data?.selected + 1,
                  },
                })
              );
            }}
            paginCurrentPage={fileRetrievalQueryParams.page}
            paginItemsOnCurrentPage={results?.length || 0}
            paginTotalItemsCount={count}
          />
        )}
      </div>
      <ShowSnapshots snapshots={currentRowDataSnapshots ?? []} />
    </div>
  );
};

export default ScreenVideoHistoryFileRetrieval;
